import { useCallback, useSyncExternalStore } from "react";
import {
  THEME_OPTIONS,
  ThemeOptionComponent,
  ThemeOptionProp,
  ThemeOptionValue,
  themeAttr,
} from "../utils/partnerTheme";

/**
 * A Theme Studio option the partner chose (`data-zv-<component>-<prop>` on <html>,
 * set by utils/partnerTheme.ts), or "default" for As shipped — also for a value
 * this app does not implement. For React branches that change markup; a
 * stylesheet gates the same choice with the `when-theme` mixin.
 */
export const readThemeOption = <C extends ThemeOptionComponent, P extends ThemeOptionProp<C>>(
  component: C,
  prop: P
): ThemeOptionValue<C, P> => {
  if (typeof document === "undefined") return "default";
  const value = document.documentElement.getAttribute(themeAttr(component, prop));
  const known = THEME_OPTIONS[component][prop] as readonly string[];
  return (value && known.includes(value) ? value : "default") as ThemeOptionValue<C, P>;
};

/** Live: the CRM preview changes the attribute without a reload. */
export function useThemeOption<C extends ThemeOptionComponent, P extends ThemeOptionProp<C>>(
  component: C,
  prop: P
): ThemeOptionValue<C, P> {
  const attr = themeAttr(component, prop);
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof MutationObserver === "undefined" || typeof document === "undefined") return () => {};
      const observer = new MutationObserver(onChange);
      observer.observe(document.documentElement, { attributes: true, attributeFilter: [attr] });
      return () => observer.disconnect();
    },
    [attr]
  );
  const read = useCallback(() => readThemeOption(component, prop), [component, prop]);
  return useSyncExternalStore(subscribe, read, () => "default" as ThemeOptionValue<C, P>);
}
